import React, { useState, useEffect } from "react";
import { Modal, Button} from "react-bootstrap";
import SectionTitle from "../components/ui/sectionTitle/SectionTitle";
import TextInput from "../components/ui/input/TextInput";
import SelectInput from "../components/ui/input/SelectInput";
import PrimaryButton from "../components/ui/button/PrimaryButton";
import { getValidAccessToken } from "../services/oauthservice";
import ConfirmBox from "../components/modal/ConfirmBox";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import './driverModalStyle.css'
import { encryptRSA } from "../services/encrypt";

const DriverModal = ({ show, handleClose, quoteNo }) => {
    const [driverDetail, SetDriverDetail] = useState({
        driverName: '',
        driverAge: '',
        relationShip: '',
        licenseNo: ''
    });
    const [drivers, setDrivers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [errMessage, setErrMessage] = useState('')
    const [relationships, setRelationships] = useState([]);
    const [isUpdate, setIsUpdate] = useState(false);

    useEffect(() => {
        const fetchRelationships = async () => {
            try {
                const token = await getValidAccessToken();

                const res = await fetch(
                    `${process.env.REACT_APP_DIC_ADMIN_API}/v1/motor/quotes/relationships`,
                    {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json"
                        }
                    }
                );

                const data = await res.json();

                if (data.success && Array.isArray(data.data)) {
                    const formattedOptions = data.data.map(([id, name]) => ({
                        value: name,
                        id: id,
                        label: name
                    }));
                    setRelationships(formattedOptions);
                }
            } catch (err) {
                console.error("Error fetching relationships:", err);
            }
        };

        fetchRelationships();
    }, []);

    const fetchDrivers = async () => {
        try {
            const token = await getValidAccessToken();
            const response = await fetch(
                `${process.env.REACT_APP_DIC_ADMIN_API}/v1/motor/quotes/drivers?ulmNo=${encodeURIComponent(quoteNo)}`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();
            if (data.success) {
                setDrivers(data.data || []);
            } else {
                console.error("Failed to fetch drivers:", data.message);
            }
        } catch (err) {
            console.error("Error fetching drivers:", err);
        }
    };

    // Call API on initial load
    useEffect(() => {
        if (quoteNo) {
            fetchDrivers();
        }
    }, [quoteNo]);

    const handleDriver = async (deleteDriver = {}) => {
        const localDriverDetail = Object.keys(deleteDriver).length > 0
            ? deleteDriver
            : driverDetail;
        const method =
            Object.keys(deleteDriver || {}).length > 0
                ? "DELETE"
                : isUpdate
                    ? "PUT"
                    : "POST";

        try {
            const token = await getValidAccessToken(); // Get token first

            const payload = {
                ulmNo: quoteNo,
                driverName: localDriverDetail.driverName,
                driverAge: localDriverDetail.driverAge,
                relationShip: localDriverDetail.relationShip,
                licenseNo: localDriverDetail.licenseNo || ""
            };
            const encrypted = await encryptRSA(payload)
            const response = await fetch(`${process.env.REACT_APP_DIC_ADMIN_API}/v1/motor/quotes/internal-drivers`, {
                method: method,
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ encrypted })
            });
            setIsUpdate(false)
            const data = await response.json();

            if (data.success) {
                // Optionally reset form
                fetchDrivers()
                SetDriverDetail({
                    driverName: '',
                    driverAge: '',
                    relationShip: '',
                    licenseNo: ''
                })
            } else {
                console.error("Failed to add driver:", data.message || data.error);
            }
        } catch (err) {
            console.error("Error adding driver:", err);
        }
    };

    const handleDriverChange = (e) => {
        SetDriverDetail({ ...driverDetail, [e.target.name]: e.target.value });
    };

    const handleCloseConfirm = () => {
        setErrMessage('')
        setShowModal(false)
    }

    const handleEdit = (args) => {
        SetDriverDetail({
            driverName: args.drivername,
            driverAge: args.driverage,
            relationShip: args.driverrelation,
            licenseNo: args.driverlicense,
            quoteNo: args.quoteno
        })
        setIsUpdate(true)
    }

    const handleDelete = (args) => {
        const deleteDriver = {
            driverName: args.drivername,
            driverAge: args.driverage,
            relationShip: args.driverrelation,
            licenseNo: args.driverlicense,
            quoteNo: args.quoteno
        }
        handleDriver(deleteDriver)
    }

    return (
        <>
            <ConfirmBox
                show={showModal}
                message={errMessage}
                onCancel={handleCloseConfirm} />
            <Modal show={show} onHide={handleClose} centered dialogClassName="responsive-modal">
                {/* <Modal.Header closeButton>
                    <Modal.Title>Add Driver</Modal.Title>
                </Modal.Header> */}

                <Modal.Body>
                    <div className="card shadow mt-3">
                        <div className="card-body">
                            <SectionTitle title={isUpdate ? "Update" : "Add"} />
                            <div className='row'>
                                <div className='col-md-6'>
                                    <TextInput
                                        label='Name'
                                        type='text'
                                        name='driverName'
                                        value={driverDetail.driverName}
                                        onChange={handleDriverChange}
                                        placeholder='Name'
                                        required={true}
                                        requiredMsg='Please enter name'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <TextInput
                                        label='Age'
                                        type='number'
                                        name='driverAge'
                                        value={driverDetail.driverAge}
                                        onChange={handleDriverChange}
                                        placeholder='Age'
                                        required={true}
                                        requiredMsg='Please enter age'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <SelectInput
                                        label="Relationship"
                                        name="relationShip"
                                        value={driverDetail.relationShip}
                                        onChange={handleDriverChange}
                                        options={relationships}
                                        required={true}
                                        requiredMsg="Please enter relationship"
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <TextInput
                                        label='License No'
                                        type='text'
                                        name='licenseNo'
                                        value={driverDetail.licenseNo}
                                        onChange={handleDriverChange}
                                        placeholder='License No'
                                    />
                                </div>
                                <div className='col-md-9'></div>
                                <div className='col-md-3'>
                                    {isUpdate ? <PrimaryButton label="Update" onClick={() => handleDriver()}
                                        disabled={
                                            !driverDetail?.driverName.trim() ||
                                            !driverDetail?.driverAge?.toString().trim() ||
                                            !driverDetail?.relationShip.trim()
                                        } /> : <PrimaryButton label="Add" onClick={() => handleDriver()}
                                        disabled={
                                            !driverDetail.driverName.trim() ||
                                            !driverDetail?.driverAge?.toString().trim() ||
                                            !driverDetail.relationShip.trim()
                                        } />}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <table
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                marginTop: "10px",
                                minWidth: "500px", // ensures proper layout for scroll
                            }}
                        >
                            <thead>
                                <tr style={{ backgroundColor: "#f5f5f5" }}>
                                    <th style={{ border: "1px solid #ddd", textAlign: "center", padding: "8px" }}>#</th>
                                    <th style={{ border: "1px solid #ddd", textAlign: "center", padding: "8px" }}>Name</th>
                                    <th style={{ border: "1px solid #ddd", textAlign: "center", padding: "8px" }}>Age</th>
                                    <th style={{ border: "1px solid #ddd", textAlign: "center", padding: "8px" }}>Relationship</th>
                                    <th style={{ border: "1px solid #ddd", textAlign: "center", padding: "8px" }}>License No</th>
                                    <th style={{ border: "1px solid #ddd", textAlign: "center", padding: "8px" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {drivers.length > 0 ? (
                                    drivers.map((driver, index) => (
                                        <tr key={index}>
                                            <td style={{ border: "1px solid #ddd", textAlign: "right", padding: "8px" }}>{index + 1}</td>
                                            <td style={{ border: "1px solid #ddd", textAlign: "right", padding: "8px" }}>{driver.drivername}</td>
                                            <td style={{ border: "1px solid #ddd", textAlign: "right", padding: "8px" }}>{driver.driverage}</td>
                                            <td style={{ border: "1px solid #ddd", textAlign: "right", padding: "8px" }}>{driver.driverrelation}</td>
                                            <td style={{ border: "1px solid #ddd", textAlign: "right", padding: "8px" }}>{driver.driverlicense || "-"}</td>
                                            <td style={{ border: "1px solid #ddd", textAlign: "center", padding: "8px" }}>
                                                <EditIcon
                                                    style={{ cursor: 'pointer', marginRight: '8px', color: '#06923E' }}
                                                    onClick={() => handleEdit(driver)} // pass your data here
                                                />
                                                <DeleteIcon
                                                    style={{ cursor: 'pointer', color: '#FB4141' }}
                                                    onClick={() => handleDelete(driver)} // or any unique key
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: "center", padding: "8px" }}>
                                            No drivers found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <div className='row'>
                        {/* <div className='col-md-6'></div> */}
                        <div className='col-md-12'>
                            <Button variant="secondary" onClick={handleClose}>
                                Close
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DriverModal;